// Orchestration layer between the EvisioBridgeHost (parent-side bridge to the eGo iframe)
// and the Evisio2 SDK Connection (parent-side bridge to the AI iframe). Owned by
// evisio2workspaceRoot once both the signed-in AI iframe ref and the bridge host are known.
//
// Responsibilities:
//   - sdk.connect({mode:'iframeAttach', iframe}) using the MDSC-signed-in iframe
//   - register/unregister AI tools mirroring bridgeHost.getAvailableCommands() — bridge ready
//     can fire before OR after sdk connect, both paths converge on _syncTools
//   - theme sync eGo → AI iframe via BRIDGE_TOPIC.ThemeChanged → conn.setView({theme})
//   - connection observability (error/disconnected/connectionLost/resumed) logged for now
//   - clean teardown on workspace destroy

import topic from 'dojo/topic';
import lang from 'dojo/_base/lang';
// SDK is side-loaded at runtime by Evisio2SdkLoader.kickoff() — there is no .js companion
// to the .d.ts to import. Pull types only (erased at compile time so webpack never tries
// to resolve a non-existent module) and reach for the runtime via window.evisio2sdk below.
import type {
    Connection,
    ConnectionErrorEvent,
    Evisio2SDK,
    EvisioToolConfig,
    Tool,
} from "./evisio2sdk";
import EvisioBridgeHost from '../bridge/EvisioBridgeHost';
import {
    BRIDGE_TOPIC,
    EvisioBridgeCommand,
    EvisioBridgeCommandName,
    ThemeChangedPayload,
} from "../bridge/EvisioBridgeTypes";

// 5s buffer over the bridge's hardcoded 30s COMMAND_RESULT_TIMEOUT_SECONDS so the bridge
// always times out first → tool result carries a precise EvisioBridgeErrorShape kind
// instead of a generic SDK 'timeout'.
const TOOL_TIMEOUT_SECONDS = 35;

// Singleton slot — read by the RMTHIS test hook in EvisioBridgeHost so devs can
// `top.evisioTestTool.listSdkTools()` to verify the bridge↔SDK link without opening
// the AI iframe. Cleared on destroy. Multiple wiring instances would clobber, but
// the workspace root owns at most one at a time.
const SINGLETON_KEY = "__evisio2SdkWiring";

class Evisio2SdkWiring {
    private _conn: Connection | null = null;
    private _sdk: Evisio2SDK | null = null;
    private _bridgeReadySub: any = null;
    private _bridgeThemeSub: any = null;
    private _bridgeEditorOpenedSub: any = null;
    private _bridgeEditorClosedSub: any = null;
    private _registeredToolNames: string[] = [];
    // Tracked separately from _registeredToolNames so the open/close lifecycle never
    // touches the always-on set (and vice versa on iframe reload).
    private _editorScopedToolNames: string[] = [];

    constructor(private iframe: HTMLIFrameElement, private bridgeHost: EvisioBridgeHost) {}

    public async connect(): Promise<void> {
        let theme = this._readStoredTheme();

        let sdk: Evisio2SDK | undefined = (window as any).evisio2sdk;
        if (!sdk || typeof sdk.connect !== "function"){
            throw new Error("evisio2sdk not loaded on window — did Evisio2SdkLoader.kickoff() fail?");
        }
        this._sdk = sdk;

        this._conn = await sdk.connect({
            mode: "iframeAttach",
            iframe: this.iframe,
            tools: [],
            // AI iframe lives inside the companion workspace which has its own chrome,
            // so default the assistant's sidebar to sheet/off-canvas mode.
            view: { theme: theme || "auto", compactSidebar: true },
        });

        this._conn.on("error",          (e: ConnectionErrorEvent) => console.warn("[evisio2SDK] error", e && e.error));
        this._conn.on("disconnected",   () => console.warn("[evisio2SDK] disconnected"));
        this._conn.on("connectionLost", () => console.warn("[evisio2SDK] connectionLost"));
        this._conn.on("resumed",        () => console.log("[evisio2SDK] resumed"));

        // Tool registration is driven by bridge readiness. Subscribe first so we don't miss
        // a fire, then sync-call if the bridge has already emitted ready (queue is non-empty).
        this._bridgeReadySub = topic.subscribe(BRIDGE_TOPIC.Ready, lang.hitch(this, this._syncTools));
        if (this.bridgeHost.getAvailableCommands().length > 0) this._syncTools();

        this._bridgeThemeSub = topic.subscribe(BRIDGE_TOPIC.ThemeChanged, lang.hitch(this, this._onBridgeTheme));

        // Editor-scoped tools register/unregister around the email editor's open lifecycle
        // so the agent never sees getEmailDraft / setEmailDraft / etc. when there's no
        // draft to read. The bridge already gates them by Email_Editor role on the child;
        // here we additionally gate by session presence.
        this._bridgeEditorOpenedSub = topic.subscribe(BRIDGE_TOPIC.EmailEditorOpened, lang.hitch(this, this._onEditorOpened));
        this._bridgeEditorClosedSub = topic.subscribe(BRIDGE_TOPIC.EmailEditorClosed, lang.hitch(this, this._onEditorClosed));

        (window as any)[SINGLETON_KEY] = this;
    }

    public getRegisteredToolNames(): string[] {
        return this._registeredToolNames.slice();
    }

    private _syncTools(): void {
        if (!this._conn) return;

        // Re-fires on iframe reload — drop the prior registration set first.
        if (this._registeredToolNames.length){
            try { this._conn.removeTools(this._registeredToolNames); } catch (e){ console.warn("[evisio2SDK] removeTools failed", e); }
            this._registeredToolNames = [];
        }

        let available: { [k: string]: boolean } = {};
        let list = this.bridgeHost.getAvailableCommands();
        for (let i = 0; i < list.length; i++) available[list[i]] = true;

        // setTheme is bridge-internal — never exposed as an AI tool. AI theme rides on setView.
        let tools: Tool[] = [];
        if (available["openEntityView"])            tools.push(this._defineOpenEntityView());
        if (available["highlightCalendarOverview"]) tools.push(this._defineHighlightCalendarOverview());
        if (available["openEmailEditor"])           tools.push(this._defineOpenEmailEditor());

        if (!tools.length) return;
        this._conn.registerTools(tools);
        this._registeredToolNames = tools.map(t => t.name);
    }

    private _onBridgeTheme(payload: ThemeChangedPayload): void {
        if (!this._conn || !payload || !payload.theme) return;
        this._conn.setView({ theme: payload.theme });
    }

    private _onEditorOpened(): void {
        if (!this._conn) return;
        // Defense: if the role isn't available (race with role revoke?), don't register.
        if (this.bridgeHost.getAvailableCommands().indexOf("getEmailDraft") === -1) return;
        // Drop a stale registration first — happens if the close event was missed
        // (rare, but the bridge can drop events on iframe reloads).
        if (this._editorScopedToolNames.length){
            try { this._conn.removeTools(this._editorScopedToolNames); } catch { /* ignore */ }
            this._editorScopedToolNames = [];
        }
        let tools: Tool[] = [
            this._defineGetEmailDraft(),
            this._defineSetEmailDraft(),
            this._defineAppendEmailBody(),
            this._defineListEmailAttachments(),
            this._defineGetEmailAttachment(),
        ];
        this._conn.registerTools(tools);
        this._editorScopedToolNames = tools.map(t => t.name);
    }

    private _onEditorClosed(): void {
        if (!this._conn || !this._editorScopedToolNames.length) return;
        try { this._conn.removeTools(this._editorScopedToolNames); }
        catch (e){ console.warn("[evisio2SDK] removeTools (editor-scoped) failed", e); }
        this._editorScopedToolNames = [];
    }

    private _readStoredTheme(): "light" | "dark" | "auto" | null {
        let toggler: any = (window as any).themeToggler;
        if (!toggler || typeof toggler.getTheme !== "function") return null;
        try {
            let stored = toggler.getTheme(false);
            if (stored === "light" || stored === "dark" || stored === "auto") return stored;
        } catch (e){ /* ignore */ }
        return null;
    }

    private _defineOpenEntityView(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "openEntityView",
            displayName: "Show entity details",
            description: "Show the user detailed information about a specific record (booking, project, contact, artist, etc.) when they reference it by ID and ask to see more, view, inspect, or open it. Opens the entity-view tab in the eGo workspace. entityClass is optional — resolved server-side via Evisio2__Helpers_TrySearchClassnameByEntityId when omitted, so any ID works.",
            schema: {
                parameters: {
                    type: "object",
                    properties: {
                        entityId:    { type: "string", description: "PIT id of the record (hex:hex format, e.g. '0000038e:0000000c'). NEVER pass a name like 'Musikverein Wien' — resolve names to IDs via a search tool first." },
                        entityClass: { type: "string", description: "Optional class hint, snake_case identifier (e.g. \"ev_booking\", \"ev_project\"). Omit if unknown — resolved automatically." },
                        label:       { type: "string", description: "Optional tab label." },
                    },
                    required: ["entityId"],
                },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: (args: any) => this._runBridgeCommand("openEntityView", {
                entityId:    args && args.entityId,
                entityClass: args && args.entityClass,
                label:       args && args.label,
            }),
        };
        return this._sdk!.defineTool(config);
    }

    private _defineHighlightCalendarOverview(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "highlightCalendarOverview",
            displayName: "Highlight booking in calendar",
            description: "Navigate the eGo workspace to the calendar agenda view and scroll-highlight a specific ev_booking by ID. The ID is server-validated as an ev_booking before navigation; non-booking IDs fail fast with kind 'notABooking'.",
            schema: {
                parameters: {
                    type: "object",
                    properties: {
                        entityId: { type: "string", description: "PIT id of the ev_booking to highlight (hex:hex format, e.g. '0000038e:0000000c'). NEVER pass a name — resolve names to IDs via a search tool first." },
                    },
                    required: ["entityId"],
                },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: (args: any) => this._runBridgeCommand("highlightCalendarOverview", {
                entityId: args && args.entityId,
            }),
        };
        return this._sdk!.defineTool(config);
    }

    private _defineOpenEmailEditor(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "openEmailEditor",
            displayName: "Open email editor",
            description: "Open the eGo email editor dialog, optionally prefilled with subject, body, and recipients. contextClass + contextId together attach an entity context to the mail history; partial pairs are ignored.",
            schema: {
                parameters: {
                    type: "object",
                    properties: {
                        subject:       { type: "string", description: "Optional subject line." },
                        htmlBody:      { type: "string", description: "Optional HTML body (sanitized by the editor). Wins over plainTextBody when both are set." },
                        plainTextBody: { type: "string", description: "Optional plain-text body. Escaped and newline-converted before insertion." },
                        to:            { type: "string", description: "Optional comma-separated list of To: addresses." },
                        cc:            { type: "string", description: "Optional comma-separated list of CC: addresses." },
                        bcc:           { type: "string", description: "Optional comma-separated list of BCC: addresses." },
                        contextClass:  { type: "string", description: "Optional context entity class, snake_case identifier (e.g. 'ev_booking'). Must be paired with contextId." },
                        contextId:     { type: "string", description: "Optional context entity PIT id (hex:hex format, e.g. '0000038e:0000000c'). Must be paired with contextClass." },
                    },
                },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: (args: any) => this._runBridgeCommand("openEmailEditor", {
                subject:       args && args.subject,
                htmlBody:      args && args.htmlBody,
                plainTextBody: args && args.plainTextBody,
                to:            this._splitCsv(args && args.to),
                cc:            this._splitCsv(args && args.cc),
                bcc:           this._splitCsv(args && args.bcc),
                contextClass:  args && args.contextClass,
                contextId:     args && args.contextId,
            }),
        };
        return this._sdk!.defineTool(config);
    }

    // Editor-scoped tools — registered only while the email editor dialog is open
    // (see _onEditorOpened / _onEditorClosed). Descriptions lead with trigger phrases
    // because the baseline agent model (mistral-small-4) does best with explicit
    // "when the user says X" cues.

    private _defineGetEmailDraft(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "getEmailDraft",
            displayName: "Read current email draft",
            description: "Read the current email draft when the user asks what's in the email, asks you to review it, mentions the current subject/body/recipients, or you need the existing state before changing anything. Returns the subject, body HTML, and to/cc/bcc lists. Only callable while the email editor dialog is open.",
            schema: {
                parameters: { type: "object", properties: {} },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: () => this._runBridgeCommand("getEmailDraft", {}),
        };
        return this._sdk!.defineTool(config);
    }

    private _defineSetEmailDraft(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "setEmailDraft",
            displayName: "Replace email draft fields",
            description: "Replace fields of the current email draft when the user asks you to change the subject, rewrite the body, replace the content, or fill in a draft. Pass only the fields you want to overwrite — omitted fields stay as-is. Clear a field by passing an empty string. Recipients (to/cc/bcc) are NOT writable here; the user edits those manually. Only callable while the email editor dialog is open.",
            schema: {
                parameters: {
                    type: "object",
                    properties: {
                        subject:  { type: "string", description: "Optional. New subject line. Pass \"\" to clear. Omit to keep existing." },
                        bodyHtml: { type: "string", description: "Optional. New body HTML (sanitized by the editor). Pass \"\" to clear. Omit to keep existing. Use appendEmailBody instead if you want to add to the existing body without replacing." },
                    },
                },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: (args: any) => this._runBridgeCommand("setEmailDraft", {
                subject:  (args && typeof args.subject === "string") ? args.subject : undefined,
                bodyHtml: (args && typeof args.bodyHtml === "string") ? args.bodyHtml : undefined,
            }),
        };
        return this._sdk!.defineTool(config);
    }

    private _defineAppendEmailBody(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "appendEmailBody",
            displayName: "Append HTML to email body",
            description: "Add HTML to the END of the current email body when the user asks you to append a paragraph, add a signature, extend the email, or add more content without replacing what's there. Use this instead of setEmailDraft when you want to keep the existing body. Only callable while the email editor dialog is open.",
            schema: {
                parameters: {
                    type: "object",
                    properties: {
                        html: { type: "string", description: "HTML to append to the end of the current body." },
                    },
                    required: ["html"],
                },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: (args: any) => this._runBridgeCommand("appendEmailBody", { html: args && args.html }),
        };
        return this._sdk!.defineTool(config);
    }

    private _defineListEmailAttachments(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "listEmailAttachments",
            displayName: "List email attachments",
            description: "List the files currently attached to the email when the user asks what's attached, references an attached document by name, or needs the list before reading one. Returns id, filename, and mime for each attachment. Use getEmailAttachment with the id to read a file's contents. Only callable while the email editor dialog is open.",
            schema: {
                parameters: { type: "object", properties: {} },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            handler: () => this._runBridgeCommand("listEmailAttachments", {}),
        };
        return this._sdk!.defineTool(config);
    }

    private _defineGetEmailAttachment(): Tool {
        let config: EvisioToolConfig<any, any> = {
            name: "getEmailAttachment",
            displayName: "Fetch email attachment bytes",
            description: "Fetch the bytes of one email attachment when the user asks you to read, summarize, or analyze an attached file. Pass the id returned by listEmailAttachments. The file is delivered into the conversation as a tool-result file. Only callable while the email editor dialog is open.",
            schema: {
                parameters: {
                    type: "object",
                    properties: {
                        id: { type: "string", description: "Attachment id from listEmailAttachments." },
                    },
                    required: ["id"],
                },
            },
            execution: { timeoutSeconds: TOOL_TIMEOUT_SECONDS, requiresUserConsent: false },
            // Custom handler — unwraps the bridge envelope and decodes base64 → ArrayBuffer
            // to match the SDK's EvisioFileResult contract. Errors still flow through the
            // _runBridgeCommand-style {ok:false, error} shape so the agent gets the same
            // shape as the other tools.
            handler: async (args: any) => {
                let bridgeResult = await this._runBridgeCommand("getEmailAttachment", { id: args && args.id });
                if (!bridgeResult.ok) return bridgeResult;
                let r = bridgeResult.result;
                if (!r || typeof r.base64 !== "string"){
                    return { ok: false, error: { kind: "attachmentNotFound", message: "bridge returned no attachment content" } };
                }
                let bytes: ArrayBuffer;
                try { bytes = this._base64ToArrayBuffer(r.base64); }
                catch (e: any){
                    return { ok: false, error: { kind: "handlerThrew", message: "failed to decode attachment base64: " + ((e && e.message) || String(e)) } };
                }
                return { kind: "file", name: r.name || "", mime: r.mime || "application/octet-stream", bytes: bytes, durable: false };
            },
        };
        return this._sdk!.defineTool(config);
    }

    private _base64ToArrayBuffer(b64: string): ArrayBuffer {
        // atob is safe in all browsers we support (no Node SSR path on this code).
        let binary = atob(b64);
        let len = binary.length;
        let bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        return bytes.buffer;
    }

    private _splitCsv(s: any): string[] | undefined {
        if (typeof s !== "string" || !s) return undefined;
        let parts = s.split(",").map(p => p.trim()).filter(p => p.length > 0);
        return parts.length ? parts : undefined;
    }

    // Bridge errors carry { kind, message } — surface them inside the tool result so the
    // LLM sees WHY a call failed (notABooking, roleDenied, widgetNotFound) instead of an
    // opaque handlerThrew from the SDK. Don't re-throw: throwing collapses the error to
    // SDK's generic handlerThrew + stringified cause.
    private async _runBridgeCommand(type: EvisioBridgeCommandName, payload: any): Promise<any> {
        try {
            let result = await this.bridgeHost.sendCommandAwaitingResult({
                kind: "evisio2:command",
                type: type,
                payload: payload,
            } as EvisioBridgeCommand);
            return { ok: true, result: result };
        } catch (e: any) {
            return { ok: false, error: { kind: (e && e.kind) || "unknown", message: (e && e.message) || String(e) } };
        }
    }

    public destroy(): void {
        if (this._bridgeReadySub){ try { this._bridgeReadySub.remove(); } catch { /* ignore */ } this._bridgeReadySub = null; }
        if (this._bridgeThemeSub){ try { this._bridgeThemeSub.remove(); } catch { /* ignore */ } this._bridgeThemeSub = null; }
        if (this._bridgeEditorOpenedSub){ try { this._bridgeEditorOpenedSub.remove(); } catch { /* ignore */ } this._bridgeEditorOpenedSub = null; }
        if (this._bridgeEditorClosedSub){ try { this._bridgeEditorClosedSub.remove(); } catch { /* ignore */ } this._bridgeEditorClosedSub = null; }
        if (this._conn){ try { this._conn.close(); } catch { /* ignore */ } this._conn = null; }
        this._sdk = null;
        this._registeredToolNames = [];
        this._editorScopedToolNames = [];
        if ((window as any)[SINGLETON_KEY] === this){
            try { delete (window as any)[SINGLETON_KEY]; } catch { (window as any)[SINGLETON_KEY] = undefined; }
        }
    }
}

export default Evisio2SdkWiring;
